// packages/web-ui/src/api/clients/files.ts
//
// Story W3-S2 AC #11 — `filesApi.getContent` placeholder. Returns synthetic
// in-memory content so the W3-S2 editor surface has something to render.
// The real `GET/PUT /api/v1/files/content` integration lands in W3-S4.
//
// Story W3-S5 (additive) — adds `getTree` and `rename` wrappers consumed by
// the Files ContextPanel file-tree (`components/file-tree/`,
// `panels/files/`, `state/files.ts`). Both are wire-only and return
// `Result<T, ApiError>`. The W3-S4 save-gate agent shares this file —
// existing exports MUST NOT be renamed (additive contract).
//
// Story W3-S4 (additive) — adds `saveFile` (PUT /files/content with optional
// `?force=true` gate-bypass) and `getContentReal` (real GET /files/content
// for silent-reload on `files:changed{external}`). The W3-S2 placeholder
// `getContent` is preserved for back-compat.

import type { Result } from '@tessa-lean/base';
import { apiCall, type ApiContext, type ApiError } from '../client.js';

export interface FileContentResponse {
  /** Workspace-relative path echoed back from the server. */
  path: string;
  /** UTF-8 encoded file body. */
  content: string;
  /** Encoding name. v1 always `'UTF-8'`. */
  encoding: string;
  /** ISO-8601 last-saved time, or `null` for an unsaved/new buffer. */
  lastSavedAt: string | null;
}

/**
 * W3 fix-pass CRITICAL-BUG — replaces the W3-S2 synthetic placeholder
 * with a real `GET /api/v1/files/content?path=…` call. The SPA-shape
 * `FileContentResponse` carries `lastSavedAt` for status-bar rendering;
 * the wire response only knows about `content/encoding/path`, so we
 * normalise here. `lastSavedAt` is `null` until a save round-trip lands.
 */
export async function getContent(
  ctx: ApiContext,
  path: string,
): Promise<Result<FileContentResponse, ApiError>> {
  const raw = await getContentReal(ctx, path);
  if (!raw.ok) return raw;
  return {
    ok: true,
    value: {
      path: raw.value.path,
      content: raw.value.content,
      encoding: raw.value.encoding ?? 'UTF-8',
      lastSavedAt: null,
    },
  };
}

// ---- W3-S5 additions ----------------------------------------------------

/** Tree-node shape returned by `GET /api/v1/files/tree`. */
export interface FileTreeNode {
  /** Workspace-relative path. */
  path: string;
  /** Basename. */
  name: string;
  /** `'file'` for leaves, `'directory'` for directories. */
  kind: 'file' | 'directory';
  /** Lowercase extension (no leading dot) — present iff `kind === 'file'`. */
  ext?: string;
  /** Bytes — present iff `kind === 'file'`. */
  size?: number;
  /** Server-side aggregated diagnostics — `true` when ≥ 1 error. */
  hasErrors?: boolean;
  /** Recursive children — directories only. */
  children?: FileTreeNode[];
}

export interface GetTreeInput {
  /** Workspace-relative root, default `/`. */
  root?: string;
  /** Recursion depth, default unbounded (server caps at 32). */
  depth?: number;
}

/**
 * Fetch the workspace file tree. Empty input → `GET /api/v1/files/tree`.
 * `root` and `depth` are appended as `URLSearchParams`.
 */
export function getTree(
  ctx: ApiContext,
  input: GetTreeInput = {},
): Promise<Result<FileTreeNode[], ApiError>> {
  const params = new URLSearchParams();
  if (typeof input.root === 'string' && input.root.length > 0) {
    params.set('root', input.root);
  }
  if (typeof input.depth === 'number') {
    params.set('depth', String(input.depth));
  }
  const qs = params.toString();
  const path = qs.length > 0 ? `/api/v1/files/tree?${qs}` : '/api/v1/files/tree';
  return apiCall<undefined, FileTreeNode[]>(ctx, 'GET', path);
}

export interface RenameFileInput {
  /** Old workspace-relative path. */
  path: string;
  /** New workspace-relative path. */
  newPath: string;
}

export interface RenameFileResponse {
  written: true;
}

interface RenameWireBody extends RenameFileInput {
  op: 'rename';
}

/**
 * Rename a file via `PUT /api/v1/files/content` with the `op: 'rename'`
 * discriminator. Returns `Ok({written: true})` on success or the server's
 * `ApiError` envelope.
 */
export function renameFile(
  ctx: ApiContext,
  input: RenameFileInput,
): Promise<Result<RenameFileResponse, ApiError>> {
  return apiCall<RenameWireBody, RenameFileResponse>(ctx, 'PUT', '/api/v1/files/content', {
    op: 'rename',
    path: input.path,
    newPath: input.newPath,
  });
}

// ---- W3-S4 additions ----------------------------------------------------

/** Server response shape for `GET /api/v1/files/content?path=…` — wire shape (per `FileContentResponseSchema`). */
export interface RawFileContentResponse {
  path: string;
  content: string;
  encoding?: string;
}

/**
 * Real `GET /api/v1/files/content?path=<path>` — used by the W3-S4 save-gate
 * silent-reload + conflict-resolution paths. The W3-S2 `getContent`
 * placeholder is preserved for back-compat with existing callers; new
 * call-sites should prefer `getContentReal` once the server route lands.
 */
export function getContentReal(
  ctx: ApiContext,
  path: string,
): Promise<Result<RawFileContentResponse, ApiError>> {
  const qs = `?path=${encodeURIComponent(path)}`;
  return apiCall<undefined, RawFileContentResponse>(
    ctx,
    'GET',
    `/api/v1/files/content${qs}`,
  );
}

/** Body for `PUT /api/v1/files/content` (save). */
export interface SaveFileInput {
  path: string;
  content: string;
  /** When `true`, bypasses the server's pre-commit validation gate. */
  force?: boolean;
}

/** 200 success shape for `saveFile`. */
export interface SaveFileSuccess {
  written: boolean;
  path: string;
}

/**
 * Pre-commit-gate failure envelope — HTTP 409 + `code: 'VALIDATION_BLOCKED'`
 * with the offending diagnostics carried in `detail`. The diagnostics array
 * is opaque (`unknown[]`) at this boundary; callers cast to the LSP-shape
 * `Diagnostic[]` (from `@tessa-lean/base/api-schemas`) at render time.
 */
export interface ValidationBlockedError extends ApiError {
  code: 'VALIDATION_BLOCKED';
  detail: { diagnostics: unknown[] };
}

/**
 * `PUT /api/v1/files/content` — atomic write through the server's
 * pre-commit gate (Arch §6.7). On a gate failure the wrapper returns
 * `Err({code: 'VALIDATION_BLOCKED', detail: {diagnostics}})` so the SPA can
 * render `<PreCommitGateDialog>`. Setting `force: true` appends `?force=true`
 * which bypasses the gate AND triggers a `system:notice` audit-log line
 * server-side (epic AC #3).
 *
 * No throws cross the package boundary (CLAUDE.md "Coding Standards");
 * network failures map to `Err({code: 'NETWORK_ERROR', ...})` via `apiCall`.
 */
export function saveFile(
  ctx: ApiContext,
  input: SaveFileInput,
): Promise<Result<SaveFileSuccess, ApiError>> {
  const qs = input.force === true ? '?force=true' : '';
  return apiCall<{ path: string; content: string }, SaveFileSuccess>(
    ctx,
    'PUT',
    `/api/v1/files/content${qs}`,
    { path: input.path, content: input.content },
  );
}

/** Convenience barrel matching the rest of the api/clients/* family. */
export const filesApi = {
  getContent,
  getContentReal,
  getTree,
  rename: renameFile,
  saveFile,
};

export type FilesApi = typeof filesApi;
